import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppHttpService } from './app-http.service';
import { ImageGeoref } from '../helpers/reference-layer-image';

export type ReferenceLayerSourceType = 'geojson_upload' | 'arcgis_url' | 'kml_url' | 'image_upload' | 'published_catalog';

export interface ReferenceLayerSummary {
  id: string;
  name: string;
  source_type: ReferenceLayerSourceType;
  source_url: string | null;
  feature_count: number;
  extent: { xmin: number; ymin: number; xmax: number; ymax: number } | null;
  source_filename: string | null;
  /** Present only for a signed, read-only image_upload overlay. */
  image_georef?: ImageGeoref | null;
  /** Rows already represented by a map source stay in the published catalog. */
  map_source_id?: number | null;
  map_withdrawn?: boolean;
  /** Stable overlay palette slot (0..7); null on rows created before this existed. */
  palette_index?: number | null;
  created_at: string;
  updated_at: string;
}

export interface ReferenceLayerDetail extends ReferenceLayerSummary {
  geojson: any;
}

export interface CreateReferenceLayerPayload {
  name: string;
  source_type: ReferenceLayerSourceType;
  source_url?: string;
  geojson?: any;
  source_filename?: string | null;
}

export interface UpdateReferenceLayerPayload {
  name?: string;
  source_url?: string | null;
  geojson?: any;
  source_filename?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ProjectReferenceLayerService {
  // Emitted after a successful create/delete so the list view refreshes without
  // plumbing the event through @Output bindings across the side-panel.
  readonly listChanged = new Subject<void>();

  constructor(private http: AppHttpService) {}

  list(projectId: string): Observable<ReferenceLayerSummary[]> {
    return this.http
      .getService(`/v1/project/${projectId}/reference-layer`, { start: 0, total: 100 })
      .pipe(map((resp: any) => (resp?.data as ReferenceLayerSummary[]) || []));
  }

  get(projectId: string, id: string): Observable<ReferenceLayerDetail> {
    return this.http
      .getService(`/v1/project/${projectId}/reference-layer/${id}`)
      .pipe(map((resp: any) => resp?.data as ReferenceLayerDetail));
  }

  create(projectId: string, payload: CreateReferenceLayerPayload): Observable<ReferenceLayerDetail> {
    return this.http
      .postService(`/v1/project/${projectId}/reference-layer`, payload)
      .pipe(map((resp: any) => resp?.data as ReferenceLayerDetail));
  }

  /**
   * Upload a KML or KMZ file to create a new reference layer. The backend
   * stores the file on S3 and returns a summary with `source_type='kml_url'`
   * whose `source_url` is a signed S3 URL that ArcGIS KMLLayer can consume.
   */
  upload(
    projectId: string,
    file: File,
    name: string,
  ): Observable<ReferenceLayerSummary> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    fd.append('name', name);
    return this.http
      .postService(`/v1/project/${projectId}/reference-layer/upload`, fd)
      .pipe(map((resp: any) => resp?.data as ReferenceLayerSummary));
  }

  update(
    projectId: string,
    id: string,
    payload: UpdateReferenceLayerPayload,
  ): Observable<ReferenceLayerSummary> {
    return this.http
      .patchService(`/v1/project/${projectId}/reference-layer/${id}`, payload)
      .pipe(map((resp: any) => resp?.data as ReferenceLayerSummary));
  }

  delete(projectId: string, id: string): Observable<void> {
    return this.http
      .deleteService(`/v1/project/${projectId}/reference-layer/${id}`)
      .pipe(map(() => undefined));
  }

  /**
   * True when the web colors this summary as a legacy overlay today: it's
   * not bound to a published catalog source and not withdrawn. Mirrors the
   * API's own exclusion (ProjectReferenceLayerService.paletteIndexFrom) and
   * MapReferenceLayerService.appendLegacySummaries's skip condition.
   */
  isLegacyColored(summary: ReferenceLayerSummary): boolean {
    return !summary.map_source_id && !summary.map_withdrawn;
  }

  /**
   * Fallback palette-slot ordinal for a legacy summary with no persisted
   * `palette_index`: the row's position among the *legacy-colored* summaries
   * only (see isLegacyColored), in the given list's order. Callers must not
   * derive this from a mixed list that also holds published catalog
   * layers — a catalog layer occupying an earlier slot would shift every
   * legacy row's index, recoloring overlays that never changed position
   * among themselves. Returns 0 if `id` isn't found among the eligible rows.
   */
  legacyPaletteOrdinal(summaries: ReferenceLayerSummary[], id: string): number {
    const legacy = (summaries || []).filter((s) => this.isLegacyColored(s));
    const idx = legacy.findIndex((s) => s.id === id);
    return idx >= 0 ? idx : 0;
  }
}
